import React, { ChangeEvent, FC } from "react";
import styles from "./TimeInput.module.css";

export type TimeValue = {
    hours: string; // Hours (00-23)
    minutes: string; // Minutes (00-59)
};

interface TimeInputProps {
    label?: string;
    value: TimeValue | null; // Value as an object of hours and minutes or null
    onChange: (value: TimeValue | null) => void;
    placeholder?: string;
    errorMessage?: string;
    id: string;
    disabled?: boolean; // New disabled prop
}

const TimeInput: FC<TimeInputProps> = ({
    label,
    value,
    onChange,
    placeholder = "",
    errorMessage = "",
    id,
    disabled = false, // Default to false
}) => {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!disabled) {
            const timeString = event.target.value;
            if (timeString) {
                // Split the time string (HOURS:MINUTES)
                const [utcHours, utcMinutes] = timeString.split(":");

                // Ensure hours and minutes are in UTC without any adjustments
                const adjustedHours = utcHours.padStart(2, "0");
                const adjustedMinutes = utcMinutes.padStart(2, "0");

                // Send hours and minutes directly to onChange in UTC format
                onChange({ hours: adjustedHours, minutes: adjustedMinutes });
            } else {
                // If input is cleared, pass null to indicate no time selected
                onChange(null);
            }
        }
    };

    const handleReset = () => {
        if (!disabled) {
            onChange(null);
        }
    };

    const isValid = !errorMessage;

    // Display the time value directly in UTC format for the input field
    const utcTimeString = value ? `${value.hours}:${value.minutes}` : "";

    return (
        <div className={styles.container}>
            {label && <label htmlFor={id} className={styles.label}>{label}</label>}
            <div className={styles.inputWrapper}>
                <input
                    id={id}
                    className={`${styles.timeInput} ${isValid ? "" : styles.invalid}`}
                    type="time"
                    value={utcTimeString}
                    onChange={handleChange}
                    placeholder={placeholder}
                    aria-invalid={!isValid}
                    aria-describedby={!isValid ? `${id}-error` : undefined}
                    disabled={disabled}
                />
                {/* Add reset button when the input has a value */}
                {utcTimeString && (
                    <button
                        type="button"
                        onClick={handleReset}
                        className={styles.resetButton}
                        aria-label="Reset time input"
                        disabled={disabled}
                    >
                        &#x2715; {/* Close (X) icon for reset */}
                    </button>
                )}
            </div>
            {!isValid && (
                <span id={`${id}-error`} className={styles.errorMessage}>
                    {errorMessage}
                </span>
            )}
        </div>
    );
};

export default TimeInput;
